import { copyToClipboard } from "@/helpers/copyToClipboard";
import { buttonPadding } from "@/themeOptions";
import { CheckRounded, CopyAllRounded } from "@mui/icons-material";
import { Alert, Button, Snackbar, Tooltip } from "@mui/material";
import { useEffect, useState } from "react";

interface SLCopyToClipboardButtonProps {
  text: string;
  buttonText?: string;
  tooltipText?: string;
  successMessage?: string;
  variant?: "text" | "outlined" | "contained";
  color?: "primary" | "secondary" | "error" | "warning" | "info" | "success";
  size?: "small" | "medium" | "large";
  startIcon?: React.ReactNode;
  sx?: object;
  anchorOrigin?: {
    vertical: "top" | "bottom";
    horizontal: "left" | "center" | "right";
  };
  iconOnly?: boolean;
  isButtonGroup?: boolean;
}

export const SLCopyToClipboardButton = ({
  text,
  buttonText = text,
  tooltipText = "Kopieren",
  successMessage = "In Zwischenablage kopiert.",
  variant = "contained",
  color = "secondary",
  size = "large",
  startIcon = <CopyAllRounded fontSize="inherit" />,
  sx = {},
  anchorOrigin = { vertical: "bottom", horizontal: "center" },
  iconOnly = false,
  isButtonGroup = false,
}: SLCopyToClipboardButtonProps) => {
  const [open, setOpen] = useState(false);

  const handleClose = (
    event?: React.SyntheticEvent | Event,
    reason?: string,
  ) => {
    // Allow timeout and manual close, but prevent clickaway
    if (reason === "clickaway") {
      return;
    }
    setOpen(false);
  };

  const handleCopyClick = async () => {
    const success = await copyToClipboard(text);
    if (success) {
      setOpen(true);
    }
  };

  // Additional auto-hide with useEffect as backup
  useEffect(() => {
    if (open) {
      const timer = setTimeout(() => {
        setOpen(false);
      }, 6000);

      return () => clearTimeout(timer);
    }
  }, [open]);

  return (
    <>
      <Tooltip title={tooltipText}>
        <Button
          size={size}
          variant={isButtonGroup ? undefined : variant}
          color={isButtonGroup ? undefined : color}
          startIcon={iconOnly ? undefined : startIcon}
          aria-label={iconOnly ? tooltipText : undefined}
          onClick={handleCopyClick}
          sx={{
            padding: buttonPadding,

            ...sx,
          }}
        >
          {iconOnly ? startIcon : buttonText}
        </Button>
      </Tooltip>

      <Snackbar
        open={open}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={anchorOrigin}
      >
        <Alert
          onClose={handleClose}
          severity="success"
          sx={{
            width: "100%",
            backgroundColor: (th) => th.palette.success.main,
          }}
          icon={
            <CheckRounded
              fontSize="inherit"
              sx={{ color: (th) => th.palette.success.light }}
            />
          }
        >
          {successMessage}
        </Alert>
      </Snackbar>
    </>
  );
};
